import { DateField, TextField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  notify,
  Row,
  Title,
  useConfirmation,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useManageRedistributionServicePricingPolicies } from '~/purchase/api';
import {
  RedistributionServiceOneTyreDeliveryTable,
  useRedistributionServicePricingPolicyForm,
} from '~/purchase/components';
import { RedistributionServicePricingPolicyHelpers } from '~/purchase/helpers';
import {
  PP_CUSTOM_CONSTRAINT,
  redistributionServicePricingForm,
} from '~/purchase/schemas';
import { usePricingPolicyErrorTranslation } from '~/common/hooks';
import { IRedistributionServicePricingForm } from '~/purchase/types';

type CommonFormProps = {
  initialValues?: IRedistributionServicePricingForm;
};

type ApplicationStepFormProps = CommonFormProps & {
  onSubmit: (data: IRedistributionServicePricingForm) => void;
  isSubmitting: boolean;
  customerId: string;
  policyGroupId?: number;
};

const ApplicationStepForm: React.FC<ApplicationStepFormProps> = ({
  onSubmit,
  initialValues,
  isSubmitting,
  customerId,
  policyGroupId,
}) => {
  const { t } = useI18n();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();
  const pricingPolicyForm = useRedistributionServicePricingPolicyForm();
  const methods = useForm<IRedistributionServicePricingForm>({
    defaultValues:
      initialValues ??
      RedistributionServicePricingPolicyHelpers.getInitialValues(
        pricingPolicyForm.supplierTypes.optionItems,
      ),
    resolver: zodResolver(redistributionServicePricingForm),
  });
  const ask = useConfirmation();
  const tyreTypeColumns =
    RedistributionServicePricingPolicyHelpers.getTyreTypeColumns(
      t,
      pricingPolicyForm.vehicleTypeGroups.data?.data ?? [],
      true,
    );
  const manageServicePricingPolicies =
    useManageRedistributionServicePricingPolicies({
      checkOverlap: { enabled: true },
    });
  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData) => {
        manageServicePricingPolicies.checkOverlap.mutate(
          {
            customerIds: [customerId],
            startDate: DateUtils.formatTimestamp(formData.startDate, {
              customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
            }),
            endDate: formData.endDate
              ? DateUtils.formatTimestamp(formData.endDate, {
                  customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
                })
              : null,
            policyGroupId: policyGroupId,
          },
          {
            onError: (error) => {
              notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
                id: 'toast-error-redistribution-service-pricing-policy-overlap-check',
              });
            },
            onSuccess: () => {
              onSubmit(formData);
            },
          },
        );
      }}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
      id={RedistributionServicePricingPolicyHelpers.FORM_ID}
    >
      <Card>
        <Card.Header>
          <Card.Title>
            <Title heading='xl' weight='bold'>
              <Trans i18nKey='app.pricing_policy.redistribution_service.form.title' />
            </Title>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col sm={12}>
              <TextField
                name='name'
                label={
                  <Trans i18nKey='app.pricing_policy.redistribution_service.form.name' />
                }
                type='text'
                maxLength={PP_CUSTOM_CONSTRAINT.NAME_MAX_LENGTH}
                size='sm'
                required
              />
            </Col>
            <Col>
              <DateField
                name='startDate'
                label={<Trans i18nKey='app.common.start_date' />}
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            <Col>
              <DateField
                name='endDate'
                label={<Trans i18nKey='app.common.end_date' />}
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                placeholderText='dd-MM-yyyy'
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
          </Row>
        </Card.Body>
        <Card.Body>
          <Row className='mt-3'>
            <RedistributionServiceOneTyreDeliveryTable />
          </Row>
        </Card.Body>
      </Card>
      {pricingPolicyForm.mode === 'WRITE' && (
        <Card className='sticky' style={{ bottom: '5%' }}>
          <Card.Body>
            <Flex justifyContent={'between'}>
              <Button
                type='button'
                id='reset'
                variant='link'
                disabled={isSubmitting}
                onClick={() => {
                  ask({
                    title: t('app.common.messages.confirm'),
                    description: t(
                      'app.pricing_policy.redistribution_service.reset_confirmation_modal.title',
                    ),
                    confirmBtnText: t('app.common.button.confirm'),
                    cancelBtnText: t('app.common.button.cancel'),
                  }).onConfirm(() => {
                    pricingPolicyForm.supplierTypes.optionItems.forEach(
                      (supplierTypeItem) => {
                        tyreTypeColumns.forEach((vehicleTypeItem) => {
                          methods.resetField(
                            `oneTyreDelivery.supplierTypes.${supplierTypeItem.value}.tyreTypes.${vehicleTypeItem.value}.value`,
                            { defaultValue: undefined },
                          );
                        });
                        methods.resetField(
                          `oneTyreDelivery.supplierTypes.${supplierTypeItem.value}.tyreCount`,
                          { defaultValue: undefined },
                        );
                        methods.resetField(
                          `defaultValue.${supplierTypeItem.value}.value`,
                          { defaultValue: undefined },
                        );
                      },
                    );
                    methods.reset({
                      ...methods.formState.defaultValues,
                    });
                  });
                }}
              >
                <Trans i18nKey='app.common.button.reset' />
              </Button>

              <Button
                id='next'
                variant='primary'
                type='submit'
                form={RedistributionServicePricingPolicyHelpers.FORM_ID}
                children={<Trans i18nKey='app.common.button.save' />}
                isLoading={isSubmitting}
              />
            </Flex>
          </Card.Body>
        </Card>
      )}
    </FormContainer>
  );
};

export default ApplicationStepForm;
